/**
 * Production Job Card
 *
 * Individual job card showing:
 * - Job identity (SKU, quantity, due date)
 * - Routing context: Previous → Current → Next
 * - Time spent and status
 *
 * Design:
 * ┌─────────────────────────────────────────────────┐
 * │  [DUE: TODAY]                   Job: 123456-01  │
 * │  ────────────────────────────────────────────── │
 * │  SKU: Acme Widget Display Stand     QTY: 500    │
 * │                                                 │
 * │  ← PROOF-PDF  |  [PRINT-MACH]  |  PRINTS DONE → │
 * │                                                 │
 * │  Time: 01:23:45    Est: ~2:00                   │
 * └─────────────────────────────────────────────────┘
 */

export default function ProductionJobCard({
  job,
  session,
  isSelected,
  onSelect,
  formatElapsed,
  workCenter,
}) {
  const isRunning = session?.state === 'running';
  const isPaused = session?.state === 'paused';
  const isActive = isRunning || isPaused;

  // Format due date with urgency
  const formatDueDate = (dateStr) => {
    if (!dateStr) return { text: 'No date', className: 'text-gray-500' };

    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    const isToday = dateOnly.getTime() === today.getTime();
    const isTomorrow = dateOnly.getTime() === tomorrow.getTime();
    const isPast = dateOnly < today;

    if (isPast) return { text: 'OVERDUE', className: 'text-red-400 font-bold' };
    if (isToday) return { text: 'TODAY', className: 'text-green-400 font-bold' };
    if (isTomorrow) return { text: 'TOMORROW', className: 'text-yellow-400 font-bold' };

    return {
      text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      className: 'text-gray-500',
    };
  };

  const dueInfo = formatDueDate(job.dueDate);

  // Get routing context - show pending operations for this work center
  // For now, we show first pending operation as current
  const getRoutingContext = () => {
    const operations = job.operations || job.pendingOperations || [];
    const currentOp = job.currentOperation || operations[0];

    // For pending operations, we don't have previous/next in same context
    // Show current operation and indicate more if available
    return {
      previous: null, // Could be fetched from full routing if needed
      current: currentOp || { operationCode: workCenter, workCenter },
      next: operations.length > 1 ? operations[1] : null,
    };
  };

  const routing = getRoutingContext();

  // Card border style based on state
  const borderClass = isRunning
    ? 'border-green-500 bg-green-900/20'
    : isPaused
    ? 'border-yellow-500 bg-yellow-900/20'
    : isSelected
    ? 'border-indigo-500 bg-indigo-900/20'
    : 'border-gray-200 bg-slate-100 hover:border-gray-300';

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${borderClass}`}
    >
      {/* Header Row: Due Date and Job Number */}
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-medium ${dueInfo.className}`}>
          DUE: {dueInfo.text}
        </span>
        <span className="text-xs text-gray-500 font-mono">
          {job.jobNumber || job.jobId}
        </span>
      </div>

      {/* SKU Description and Quantity */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-gray-900 font-semibold truncate">
            {job.partDescription || job.description || job.partNumber || 'Unknown SKU'}
          </h3>
          {job.projectName && (
            <p className="text-sm text-gray-500 truncate">{job.projectName}</p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-lg font-bold text-gray-900">
            {job.qty || job.quantity || '—'}
          </div>
          <div className="text-xs text-gray-500">QTY</div>
        </div>
      </div>

      {/* Routing Context Row */}
      <div className="flex items-center justify-center gap-2 py-2 bg-white rounded-lg mb-3">
        {/* Previous Operation */}
        <div className="flex-1 text-center">
          {routing.previous ? (
            <span className="text-xs text-gray-500">
              <span className="text-gray-600">←</span>{' '}
              {routing.previous.operationCode || routing.previous.workCenter}
            </span>
          ) : (
            <span className="text-xs text-gray-600">—</span>
          )}
        </div>

        {/* Current Operation (highlighted) */}
        <div className="flex-1 text-center">
          <span className={`text-sm font-bold px-3 py-1 rounded ${
            isRunning
              ? 'bg-green-600 text-gray-900'
              : isPaused
              ? 'bg-yellow-600 text-gray-900'
              : 'bg-indigo-600 text-gray-900'
          }`}>
            {routing.current?.operationCode || workCenter}
          </span>
        </div>

        {/* Next Operation */}
        <div className="flex-1 text-center">
          {routing.next ? (
            <span className="text-xs text-gray-500">
              {routing.next.operationCode || routing.next.workCenter}{' '}
              <span className="text-gray-600">→</span>
            </span>
          ) : (
            <span className="text-xs text-gray-600">—</span>
          )}
        </div>
      </div>

      {/* Time Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isActive && (
            <span className={`w-2 h-2 rounded-full ${
              isRunning ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'
            }`} />
          )}
          <span className={`text-lg font-mono ${
            isRunning ? 'text-green-400' : isPaused ? 'text-yellow-400' : 'text-gray-500'
          }`}>
            {isActive ? formatElapsed(session) : '0:00'}
          </span>
        </div>

        {/* Status badge */}
        {isRunning ? (
          <span className="text-xs px-2 py-1 bg-green-600 text-gray-900 rounded-full">
            Running
          </span>
        ) : isPaused ? (
          <span className="text-xs px-2 py-1 bg-yellow-600 text-gray-900 rounded-full">
            Paused
          </span>
        ) : (
          <span className="text-xs px-2 py-1 bg-gray-700 text-gray-700 rounded-full">
            Not Started
          </span>
        )}
      </div>
    </button>
  );
}
